import { canonicalizeStatus } from '@/helpers/canonicalization'
import { findRels } from "@/helpers/rels"

/**
 * Determina se o paper está travado a espera de dependências.
 */
export function isLocked(paper) {
  return !!paper?.waitingFor?.length
}

export function createStatus(context, error, link, code, message) {
  const info = {
    type: "Status",
    ok: false,
    code: code || 704,  // Erro geral
    outcome: "Fault",
    message: message || "Falhou a tentativa de execução do procedimento.",
    details: [error.message || error],
    trace: error.stack
  }
  const status = canonicalizeStatus(context, link, {
    ok: info.ok,
    code: info.code,
    entity: info
  })
  console.error({ error: status })
  return status
}

/**
 * Este método processa as ações e as organiza em várias categorias e escopos.
 * As categorias são:
 * - page - Ações de página
 * - data - Ações do conjunto de dados selecionados na página.
 * - row - Ações executadas no registro sem a necessidade de seleção.
 *
 * @returns {Object} - Uma estrutura de ações organizada por categorias e escopos.
 * @example
 * {
 *   page: {
 *     menu: {
 *       all: [...], // Todas as ações da página
 *       main: [...], // Ações principais da página
 *       side: [...], // Ações secundárias da página
 *       rest: [...], // Outras ações da página
 *       categories: [
 *         { name: 'Category1', actions: [...] }, // Categorias personalizadas
 *         { name: 'Category2', actions: [...] },
 *         // ...
 *       ]
 *     },
 *     pins: {
 *       all: [...], // Todas as ações fixadas da página
 *       main: [...], // Ações principais fixadas da página
 *       side: [...], // Ações secundárias fixadas da página
 *       rest: [...], // Outras ações fixadas da página
 *       categories: [
 *         { name: 'Category1', actions: [...] }, // Categorias personalizadas
 *         { name: 'Category2', actions: [...] },
 *         // ...
 *       ]
 *     }
 *   },
 *   data: { /* ... * / }, // Estrutura semelhante para as ações de dados
 *   row: { /* ... * / }  // Estrutura semelhante para as ações de linha
 * }
 */
export function classifyActions(entity) {

  // Função auxiliar para filtrar e agrupar links por categoria.
  const categorizeActions = (actions) => {
    const actionsByCategory = actions.reduce((acc, action) => {
      const categoryKey = action.category === undefined ? null : action.category;
      if (!acc.has(categoryKey)) {
        acc.set(categoryKey, []);
      }
      acc.get(categoryKey).push(action);
      return acc;
    }, new Map());

    return Array.from(actionsByCategory, ([categoryName, actions]) => ({
      nome: categoryName, // Será null para links sem categoria
      actions
    }));
  };

  // Função auxiliar para destacar e agrupar ações por escopo.
  const collectActions = (actions) => {
    const mainActions = findRels(actions, 'Primary')
    const sideActions = findRels(actions, 'Secondary')
    const restActions = findRels(actions, { excludes: ['Primary', 'Secondary'] })

    const mainPins = findRels(mainActions, 'Pinned')
    const sidePins = findRels(sideActions, 'Pinned')
    const restPins = findRels(restActions, 'Pinned')

    const sortedActions = [...mainActions, ...sideActions, ...restActions]
    const sortedPins = [...mainPins, ...sidePins, ...restPins]

    return {
      menu: {
        all: findRels(sortedActions, 'Action'),
        main: findRels(mainActions, 'Action'),
        side: findRels(sideActions, 'Action'),
        rest: findRels(restActions, 'Action'),
        categories: categorizeActions(findRels(sortedActions, 'Action'))
      },
      pins: {
        all: findRels(sortedPins, 'Action'),
        main: findRels(mainPins, 'Action'),
        side: findRels(sidePins, 'Action'),
        rest: findRels(restPins, 'Action'),
        categories: categorizeActions(findRels(sortedPins, 'Action'))
      },
      // State é o Rel correto a se usar, mas para manter compatibilidade aceitamos ainda Icon.
      states: {
        all: findRels(sortedActions, 'State'),
        main: findRels(mainActions, 'State'),
        side: findRels(sideActions, 'State'),
        rest: findRels(restActions, 'State'),
        categories: categorizeActions(findRels(sortedPins, 'State'))
      }
    };
  }

  const links = entity.links || []

  const items = [
    ...findRels(links, 'Action'),
    ...findRels(links, { includes: ['State'], excludes: ['Action'] }),
  ]

  const dataItems = findRels(items, 'Data')
  const rowItems = findRels(items, 'Row')
  const cellItems = findRels(items, 'Cell')

  const identifiedPageItems = findRels(items, 'Page')
  const pageItems = items.filter(link =>
    identifiedPageItems.includes(link) ||
    (!rowItems.includes(link) && !dataItems.includes(link)))

  const allActions = {
    page: collectActions(pageItems),
    data: collectActions(dataItems),
    row: collectActions(rowItems),
    cell: collectActions(cellItems)
  }
  return allActions
}
